import { useState, useRef, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Camera, Upload, X, Download, Loader2, Image, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PhotoUploader({ requestId, onFilesChange, isPrescription = false }) {
  const { lang } = useLanguage();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [previews, setPreviews] = useState([]);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const text = lang === 'it' ? {
    upload: 'Carica foto',
    camera: 'Scatta foto',
    prescription: 'Scansione prescrizione',
    uploading: 'Caricamento...',
    uploaded: 'Caricato',
    download: 'Scarica',
    remove: 'Rimuovi',
    dragDrop: 'Trascina foto qui o clicca per caricare',
    prescriptionHint: 'Scatta o carica una foto della prescrizione medica',
    photoHint: 'Aggiungi foto per documentare la situazione',
    maxSize: 'Max 10MB per file. JPG, PNG, PDF',
    fileCount: (n) => `${n} file caricati`,
  } : {
    upload: 'Upload photo',
    camera: 'Take photo',
    prescription: 'Scan prescription',
    uploading: 'Uploading...',
    uploaded: 'Uploaded',
    download: 'Download',
    remove: 'Remove',
    dragDrop: 'Drag photos here or click to upload',
    prescriptionHint: 'Take or upload a photo of the medical prescription',
    photoHint: 'Add photos to document the situation',
    maxSize: 'Max 10MB per file. JPG, PNG, PDF',
    fileCount: (n) => `${n} files uploaded`,
  };

  const handleFileSelect = useCallback(async (selectedFiles) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    setUploading(true);

    const newPreviews = [];
    const newFiles = [];

    for (const file of selectedFiles) {
      // Create preview
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        const preview = await new Promise((resolve) => {
          reader.onload = (e) => resolve(e.target.result);
          reader.readAsDataURL(file);
        });
        newPreviews.push({ name: file.name, preview, type: 'image' });
      } else {
        newPreviews.push({ name: file.name, preview: null, type: 'pdf' });
      }

      // Upload
      try {
        const formData = new FormData();
        formData.append('file', file);
        const params = requestId ? `?request_id=${requestId}` : '';
        const { data } = await axios.post(`${API}/upload${params}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        newFiles.push(data);
      } catch (e) {
        console.error('Upload error:', e);
        toast.error(`Errore upload: ${file.name}`);
      }
    }

    setPreviews(prev => [...prev, ...newPreviews]);
    const allFiles = [...files, ...newFiles];
    setFiles(allFiles);
    if (onFilesChange) onFilesChange(allFiles);
    setUploading(false);

    if (newFiles.length > 0) {
      toast.success(`${newFiles.length} file caricati`);
    }
  }, [files, requestId, onFilesChange]);

  const handleFileInput = (e) => {
    handleFileSelect(Array.from(e.target.files));
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFileSelect(Array.from(e.dataTransfer.files));
  };

  const removeFile = (index) => {
    const newPreviews = previews.filter((_, i) => i !== index);
    const newFiles = files.filter((_, i) => i !== index);
    setPreviews(newPreviews);
    setFiles(newFiles);
    if (onFilesChange) onFilesChange(newFiles);
  };

  const downloadFile = async (fileData) => {
    try {
      const response = await axios.get(`${API}/files/${fileData.id}/download`, {
        responseType: 'blob'
      });
      const blobUrl = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = fileData.original_filename || 'file';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error('Errore download');
    }
  };

  return (
    <div className="space-y-3" data-testid="photo-uploader">
      {/* Header */}
      <div className="flex items-center gap-2">
        {isPrescription ? (
          <FileText size={16} className="text-[#FF9F0A]" />
        ) : (
          <Camera size={16} className="text-[#0A84FF]" />
        )}
        <span className="text-xs uppercase tracking-widest text-[#4A6B8A] font-bold">
          {isPrescription ? text.prescription : text.upload}
        </span>
      </div>

      {/* Hint */}
      <p className="text-[10px] text-[#6B8AB5] uppercase tracking-widest">
        {isPrescription ? text.prescriptionHint : text.photoHint}
      </p>

      {/* Upload area */}
      <div
        className="border border-dashed border-[#B8D4E8] bg-[#E8F4FD] p-6 text-center hover:border-[#FF3B30]/50 transition-all cursor-pointer"
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        data-testid="drop-zone"
      >
        {uploading ? (
          <div className="flex items-center justify-center gap-2">
            <Loader2 size={20} className="animate-spin text-[#FF3B30]" />
            <span className="text-xs text-[#4A6B8A] uppercase tracking-widest">{text.uploading}</span>
          </div>
        ) : (
          <div className="space-y-2">
            <Upload size={24} className="mx-auto text-[#6B8AB5]" />
            <p className="text-xs text-[#6B8AB5] uppercase tracking-widest">{text.dragDrop}</p>
            <p className="text-[10px] text-[#8BA3C7]">{text.maxSize}</p>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-[#B8D4E8] bg-white text-[#0D1B2A] text-xs uppercase tracking-widest hover:border-[#0A84FF] transition-all"
          data-testid="upload-btn"
        >
          <Upload size={14} />
          {text.upload}
        </button>
        <button
          type="button"
          onClick={() => cameraInputRef.current?.click()}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-[#B8D4E8] bg-white text-[#0D1B2A] text-xs uppercase tracking-widest hover:border-[#FF3B30] transition-all"
          data-testid="camera-btn"
        >
          <Camera size={14} />
          {text.camera}
        </button>
      </div>

      {/* Hidden inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept={isPrescription ? "image/*,application/pdf" : "image/*"}
        multiple
        onChange={handleFileInput}
        className="hidden"
        data-testid="file-input"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileInput}
        className="hidden"
        data-testid="camera-input"
      />

      {/* Previews */}
      {previews.length > 0 && (
        <div className="space-y-2" data-testid="file-previews">
          <span className="text-[10px] text-[#6B8AB5] uppercase tracking-widest">
            {text.fileCount(previews.length)}
          </span>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {previews.map((p, index) => (
              <div key={index} className="relative border border-[#B8D4E8] bg-white overflow-hidden group">
                {p.type === 'image' && p.preview ? (
                  <img src={p.preview} alt={p.name} className="w-full h-24 object-cover" />
                ) : (
                  <div className="w-full h-24 flex items-center justify-center">
                    <FileText size={32} className="text-[#6B8AB5]" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center gap-2">
                  {files[index] && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); downloadFile(files[index]); }}
                      className="p-1.5 bg-[#0A84FF] text-[#0D1B2A]"
                      data-testid={`download-file-${index}`}
                    >
                      <Download size={12} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                    className="p-1.5 bg-[#FF3B30] text-[#0D1B2A]"
                    data-testid={`remove-file-${index}`}
                  >
                    <X size={12} />
                  </button>
                </div>
                <div className="px-2 py-1 bg-[#E8F4FD]">
                  <p className="text-[9px] text-[#6B8AB5] truncate">{p.name}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
